/**
 * Create Feature Request Hook
 *
 * Submits a feature request via GraphQL mutation.
 */

import { useMutation } from '@tanstack/react-query';
import { axiosInstance } from '@/dataProvider/axios';
import toast from 'react-hot-toast';

const CREATE_FEATURE_REQUEST_MUTATION = `
  mutation CreateFeatureRequest($input: CreateFeatureRequestInput!) {
    createFeatureRequest(input: $input) {
      id
      userId
      headline
      description
      insertedAt
    }
  }
`;

export interface CreateFeatureRequestInput {
  headline: string;
  description: string;
}

interface FeatureRequestResponse {
  id: string;
  userId: string;
  headline: string;
  description: string;
  insertedAt: string;
}

interface CreateFeatureRequestData {
  createFeatureRequest: FeatureRequestResponse;
}

/**
 * Hook to submit a feature request.
 *
 * @example
 * const { mutate, isPending } = useCreateFeatureRequest();
 *
 * mutate({ headline: 'Dark mode', description: 'Add dark mode to settings.' });
 */
export function useCreateFeatureRequest() {
  return useMutation<FeatureRequestResponse, Error, CreateFeatureRequestInput>({
    mutationFn: async (input: CreateFeatureRequestInput) => {
      const response = await axiosInstance.post<CreateFeatureRequestData>('/graphql', {
        query: CREATE_FEATURE_REQUEST_MUTATION,
        variables: { input },
      });
      return response.data.createFeatureRequest;
    },
    onSuccess: () => {
      toast.success('Feature request received. Thanks for the idea!');
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to submit feature request. Please try again.');
    },
  });
}
